import React, { useState } from "react";
import { GraduationCap, ShieldCheck, CheckCircle2, PenLine, Lock, Globe, ScrollText } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Card, Section, Btn } from "./ui";

const LESSONS = [
  { icon: Lock, title: "Sensitive data is detected first", text: "Before any prompt reaches an AI provider, LucidGate scans it for personal data, financial details, credentials and privileged content." },
  { icon: Globe, title: "Sovereign routing protects clients", text: "When sensitive data is present, requests are kept on EU providers — US-based models are excluded automatically." },
  { icon: ScrollText, title: "Everything is logged", text: "Each request is recorded in the audit trail: what was detected, the decision taken, and which provider handled it." },
];

const QUIZ = [
  {
    q: "You need to summarise a document that contains a client's IBAN and name. What is the safest approach?",
    options: ["Paste everything — the AI needs full context", "Let LucidGate mask the sensitive tokens, or remove them yourself", "Email it to a colleague instead"],
    answer: 1,
  },
  {
    q: "When sensitive data is detected, which providers may be used?",
    options: ["Any provider, fastest wins", "EU-sovereign providers only — US providers are excluded", "Only providers the employee picks"],
    answer: 1,
  },
  {
    q: "Why does LucidGate log every request?",
    options: ["To support oversight and audit readiness", "To slow people down", "There is no reason"],
    answer: 0,
  },
];

export default function SignOff() {
  const { session, refreshUser } = useAuth();
  const signed = !!session.user.signed_at;
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [done, setDone] = useState(signed);

  const score = QUIZ.reduce((s, q, i) => (answers[i] === q.answer ? s + 1 : s), 0);
  const allAnswered = Object.keys(answers).length === QUIZ.length;

  const sign = async () => {
    const u = await api.signoff(session.user.id, { score });
    refreshUser(u);
    setDone(true);
  };

  if (done) {
    return (
      <Section title="AI Policy &amp; Training">
        <Card className="mx-auto max-w-xl p-8 text-center lg-rise">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-allow/40 bg-allow/10"><CheckCircle2 className="h-7 w-7 text-allow" /></div>
          <h2 className="mt-4 font-display text-xl font-bold">AI usage policy signed</h2>
          <p className="mt-2 text-sm text-slate-400">
            Thank you, {session.user.name}. Your acknowledgement is recorded in the evidence dossier
            {session.user.signed_at ? ` on ${new Date(session.user.signed_at).toLocaleDateString("en-GB")}` : ""}.
          </p>
          {session.user.training_score != null && (
            <p className="mt-1 font-mono text-[12px] text-slate-500">Training quiz score: {session.user.training_score}/3</p>
          )}
        </Card>
      </Section>
    );
  }

  return (
    <Section title="AI Policy &amp; Training" subtitle="A short explanation of permitted AI use — please review, complete the quiz, and sign.">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {LESSONS.map((l, i) => (
            <Card key={i} className="flex gap-4 p-5 lg-rise" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-brand/40 bg-brand/10"><l.icon className="h-5 w-5 text-brand" /></div>
              <div><h3 className="font-display text-sm font-bold">{l.title}</h3><p className="mt-1 text-sm text-slate-400">{l.text}</p></div>
            </Card>
          ))}
        </div>

        <Card className="p-6">
          <div className="mb-4 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-brand" /><h3 className="font-display text-sm font-bold">Quick knowledge check</h3></div>
          <div className="space-y-5">
            {QUIZ.map((q, i) => (
              <div key={i}>
                <div className="mb-2 text-sm font-medium text-slate-200">{i + 1}. {q.q}</div>
                <div className="space-y-1.5">
                  {q.options.map((o, oi) => {
                    const chosen = answers[i] === oi;
                    const showState = submitted && chosen;
                    const correct = q.answer === oi;
                    return (
                      <button
                        key={oi}
                        data-testid={`quiz-${i}-${oi}`}
                        onClick={() => !submitted && setAnswers({ ...answers, [i]: oi })}
                        className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors"
                        style={{
                          borderColor: showState ? (correct ? "#10B98166" : "#F43F5E66") : chosen ? "#0EA5E966" : "#1E2A3C",
                          background: chosen ? "rgba(14,165,233,0.08)" : "transparent",
                          color: chosen ? "#fff" : "#94A3B8",
                        }}
                      >
                        <span className="flex h-4 w-4 items-center justify-center rounded-full border border-current text-[9px]">{String.fromCharCode(65 + oi)}</span>
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {submitted && (
            <div className="mt-4 rounded-lg border border-subtle bg-elevated/50 px-3 py-2 text-sm text-slate-300">
              You scored <span className="font-bold text-brand">{score}/3</span>. You can still sign below.
            </div>
          )}

          <div className="mt-5 rounded-lg border border-subtle bg-elevated/40 p-4">
            <p className="text-xs text-slate-400">
              I acknowledge I have read and understood LucidGate&apos;s AI usage policy and will use AI tools
              in line with my firm&apos;s data-handling obligations. (A confirmation is sufficient for this prototype.)
            </p>
            <div className="mt-3 flex gap-2">
              {!submitted ? (
                <Btn data-testid="submit-quiz" variant="ghost" disabled={!allAnswered} onClick={() => setSubmitted(true)}>
                  <GraduationCap className="h-4 w-4" /> Submit quiz
                </Btn>
              ) : null}
              <Btn data-testid="sign-policy" onClick={sign} disabled={!submitted}>
                <PenLine className="h-4 w-4" /> Sign the AI policy
              </Btn>
            </div>
          </div>
        </Card>
      </div>
    </Section>
  );
}
